/**
 * Dashboard Service - 看板数据服务（按模块分接口）
 * 页面组件只调用此 Service 层,不直接调用 API
 */

import { config } from '../../config';
import * as dashboardMock from '../../mocks/dashboard';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import { CHANNEL_OPTIONS } from '../../constants/order';

// ===== 筛选项接口 =====

/**
 * 获取渠道下拉选项（固定来源于 CHANNEL_OPTIONS 常量，不再调用后端接口）
 */
export async function getChannelOptions() {
  return { success: true, data: CHANNEL_OPTIONS };
}

// ===== 查询请求体类型 =====

interface DashboardQueryRequest {
  channel?: string;
}

// ===== 10 个模块独立接口（POST + channel 过滤） =====

export async function getDemandVolume(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.DEMAND_VOLUME, params ?? {});
}

export async function getOutputThroughput(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.OUTPUT_THROUGHPUT, params ?? {});
}

export async function getOrderFlowStatus(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.ORDER_FLOW_STATUS, params ?? {});
}

export async function getSlaFulfillment(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.SLA_FULFILLMENT, params ?? {});
}

export async function getProcessingTime(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.PROCESSING_TIME, params ?? {});
}

export async function getQualityStock(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.QUALITY_STOCK, params ?? {});
}

/**
 * 查询单个商品的缺货原因分布（按 itemCode 过滤）
 * 用于 Quality Panel 点击 Top10 缺货商品后加载 SKU 维度的 OOS Reason Breakdown
 */
export async function getItemShortageReasons(itemCode: string, channel?: string) {
  const payload: { itemCode: string; channel?: string } = { itemCode };
  if (channel && channel !== 'all') {
    payload.channel = channel;
  }
  return httpClient.post(API_ENDPOINTS.DASHBOARD.QUALITY_STOCK_ITEM_REASONS, payload);
}

export async function getFinancial(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.FINANCIAL, params ?? {});
}

export async function getCancellation(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.CANCELLATION, params ?? {});
}

export async function getWorkforce(params?: DashboardQueryRequest) {
  return httpClient.post(API_ENDPOINTS.DASHBOARD.WORKFORCE, params ?? {});
}

// ===== 聚合方法：并行调用所有模块接口，组装成前端组件需要的数据结构 =====

export async function getDashboardData(channel?: string) {
  if (config.mock) {
    return dashboardMock.mockGetDashboardData();
  }

  const queryParams: DashboardQueryRequest = channel && channel !== 'all' ? { channel } : {};

  const results = await Promise.allSettled([
    getDemandVolume(queryParams),
    getOutputThroughput(queryParams),
    getOrderFlowStatus(queryParams),
    getSlaFulfillment(queryParams),
    getProcessingTime(queryParams),
    getQualityStock(queryParams),
    getFinancial(queryParams),
    getCancellation(queryParams),
    getWorkforce(queryParams),
  ]);

  const extractData = (result: PromiseSettledResult<any>, label: string) => {
    if (result.status === 'fulfilled') {
      return result.value?.data ?? {};
    }
    console.warn(`[Dashboard] ${label} failed:`, result.reason);
    return {};
  };

  const demandVolume = extractData(results[0], 'demandVolume');
  const outputThroughput = extractData(results[1], 'outputThroughput');
  const orderFlowStatus = extractData(results[2], 'orderFlowStatus');
  const slaFulfillment = extractData(results[3], 'slaFulfillment');
  const processingTime = extractData(results[4], 'processingTime');
  const qualityStock = extractData(results[5], 'qualityStock');
  const financial = extractData(results[6], 'financial');
  const cancellation = extractData(results[7], 'cancellation');
  const workforce = extractData(results[8], 'workforce');

  return {
    success: true,
    data: {
      // Module 1: Mission Control
      missionControl: {
        orderCompleteRate: slaFulfillment.orderCompleteRate ?? 0,
        todayTotalOrders: demandVolume.todayTotalOrders ?? 0,
        todayTotalItems: demandVolume.todayTotalUnits ?? 0,
        normalOrders: slaFulfillment.normalOrders ?? 0,
        timeoutOrders: slaFulfillment.timeoutOrders ?? 0,
        cancelledOrders: cancellation.countCancelOrder ?? 0,
        returnedOrders: outputThroughput.returnOrders ?? 0,
        processedReturnOrders: outputThroughput.processedReturnOrders ?? 0,
        waitingForHandlingReturnOrders: Math.max(0, (outputThroughput.returnOrders ?? 0) - (outputThroughput.processedReturnOrders ?? 0)),
        totalSKUs: demandVolume.todayTotalSkus ?? 0,
        avgSKUPerOrder: demandVolume.avgSkuPerOrder ?? 0,
        complexityPerOrder: demandVolume.todayTotalOrders > 0
          ? Math.round((demandVolume.todayTotalUnits ?? 0) / demandVolume.todayTotalOrders * 10) / 10
          : 0,
        hourlyOrders: (() => {
          const filled = new Array(24).fill(0);
          (demandVolume.orderByHour ?? []).forEach((item) => {
            const hour = parseInt(item.label, 10);
            if (!isNaN(hour) && hour >= 0 && hour < 24) {
              filled[hour] = item.value ?? 0;
            }
          });
          return filled;
        })(),
        timeoutOrderList: slaFulfillment.timeoutOrderList ?? [],
        cancelledOrderList: cancellation.cancelledOrderList ?? [],
        returnedOrderList: outputThroughput.returnedOrderList ?? [],
      },

      // Module 2: Fulfillment Pipeline
      pipeline: {
        waitingOrders: orderFlowStatus.waitingOrders ?? 0,
        avgWaitTime: processingTime.avgWaitingToPickTime ?? 0,
        pickingOrders: orderFlowStatus.pickingOrders ?? 0,
        avgPickingTime: processingTime.pickingAvgTime ?? 0,
        avgPickAndPackTime: processingTime.avgPickingDuration ?? 0,
        skuPickingTime: processingTime.pickingDurationPerSku ?? 0,
        completedOrders: outputThroughput.readyToDeliveryOrders ?? 0,
        returnProcessedOrders: outputThroughput.processedReturnOrders ?? 0,
        avgWaitingForDelivery: processingTime.avgWaitingForDelivery ?? 0,
        totalProcessingTime: Math.round(
          ((processingTime.avgWaitingToPickTime ?? 0) +
           (processingTime.avgPickingDuration ?? 0) +
           (processingTime.avgWaitingForDelivery ?? 0)) * 10
        ) / 10,
      },

      // Module 3: Workforce & Productivity
      workforce: {
        onlinePickers: workforce.onlinePickers ?? 0,
        idlePickers: workforce.idlePickers ?? 0,
        offlinePickers: workforce.offlinePickers ?? 0,
        avgOnlineTime: workforce.avgOnlineMinutes ?? 0,
        avgIdleTime: workforce.avgIdleMinutes ?? 0,
        weeklyOrderTrend: (workforce.weeklyPickingDurationPerUnit ?? []).map((item) => ({
          day: item.label,
          pickingDurationPerUnit: item.value,
        })),
        topPickers: (() => {
          // pickerDurationPerUnitRanking 的 value 单位为"秒/件"，需按 pickerAccount 匹配后换算为"分钟/件"
          const durationMap = new Map(
            (workforce.pickerDurationPerUnitRanking ?? []).map((p) => [p.pickerAccount, p.value])
          );
          return (workforce.pickerOrdersRanking ?? []).map((picker, index) => {
            const durationSeconds = durationMap.get(picker.pickerAccount);
            return {
              name: picker.pickerName,
              orders: picker.value,
              items: picker.units ?? 0,
              pickingDurationPerUnit: durationSeconds != null
                ? Math.round((Number(durationSeconds) / 60) * 10) / 10
                : 0,
              rank: index + 1,
            };
          });
        })(),
        pickerDurationPerUnitRanking: workforce.pickerDurationPerUnitRanking ?? [],
        ordersPerPersonPerHour: workforce.productivityOrdersPerHrPerPerson ?? 0,
        timePerOrder: Math.round(((workforce.timePerOrder ?? 0) / 60) * 10) / 10,
      },

      // Module 4: Quality Control & Stock Analysis
      quality: {
        orderShortageRate: qualityStock.orderShortageRate ?? 0,
        itemShortageRate: qualityStock.unitShortageRate ?? 0,
        substitutionRate: qualityStock.orderSubstitutionRate ?? 0,
        shortageReasons: (qualityStock.shortageReasonDistribution ?? []).map((item) => ({
          reason: item.reason,
          count: item.count,
          percentage: item.percentage,
        })),
        cancelReasons: (cancellation.cancelReasonDistribution ?? []).map((item) => ({
          reason: item.reason,
          count: item.count,
          percentage: item.percentage,
        })),
        topShortageItems: (qualityStock.shortageItemsRanking ?? []).map((item) => ({
          sku: item.itemCode,
          itemName: item.itemName,
          count: item.count,
        })),
        orderCancelRate: cancellation.orderCancelRate ?? 0,
        returnedOrders: outputThroughput.returnOrders ?? 0,
        shortageHandlingTime: processingTime.avgShortageHandlingTime ?? 0,
      },

      // Module 5: Financial & Trends
      financial: {
        channelSales: financial.channelSales ?? {},
        paymentMethods: financial.paymentMethods ?? {},
        cashOrdersByRider: financial.cashOrdersByRider ?? [],
        weeklyTrend: (demandVolume.weeklyOrderTrend ?? []).map((item) => ({
          date: item.label,
          orders: item.value,
        })),
      },
    },
  };
}
